import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ELSTIC_URL } from "../../config";

export const useOrderForm = () => {
  // Basic Form State
  const [formData, setFormData] = useState({
    user_id: "",
    plan_id: "",
    plan_type: "",
    plan_price: 0,
    total_amount: 0,
    final_amount: 0,
    payment_status: "link",
    payment_mode: "",
    amount_received: "",
    bank_name: "",
    utr_no: "",
    payment_attachment: null,
    billing_address: "",
    razor_pay_order_id: "",
    is_attorney: "",
    customer_type: "",
    end_date: "",
    company_name: "",
    gstNumber: "",
    pincode: "",
    state_name: "",
    total_tm_count: 0,
    /* On by default: almost every subscription is taxed, so leaving it off
       means a rep has to remember to tick it, and the one who forgets sends a
       quote short of the tax. Unticking is the deliberate act, not ticking. */
    gst_inclusion: true,
    /* Whether the amount the rep types already contains GST.
       `null` rather than false: this has to be answered, and a default would
       answer it for them — silently deciding whether a received figure is the
       base or the gross, which changes what the customer is billed. */
    gst_included_in_amount: null,
  });

  // Order Summary State
  const [orderSummary, setOrderSummary] = useState({
    customerName: "-",
    customerEmail: "-",
    customerMobile: "-",
    customerType: "-",
    planName: "-",
    planType: "-",
    planAmount: 0,
    totalTMCount: 0,
    finalAmount: 0,
    gstAmount: 0,
    gstRate: 0,
    totalAmount: 0,
    includeGST: true,
    attorneyDetails: [],
    end_date: "-",
  });

  // UI States
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editablePlanAmount, setEditablePlanAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingAttorney, setIsCheckingAttorney] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Data States
  const [customerList, setCustomerList] = useState([]);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [customerErrors, setCustomerErrors] = useState({});

  // Attorney States
  const [attorneyCount, setAttorneyCount] = useState(0);
  const [attorneyNumbers, setAttorneyNumbers] = useState([]);
  const [attorneyValidation, setAttorneyValidation] = useState({});
  const [attorneyValidationMessages, setAttorneyValidationMessages] = useState(
    {},
  );
  const [attorneyTMCounts, setAttorneyTMCounts] = useState({});

  return {
    formData,
    setFormData,
    orderSummary,
    setOrderSummary,
    isEditingAmount,
    setIsEditingAmount,
    editablePlanAmount,
    setEditablePlanAmount,
    isSubmitting,
    setIsSubmitting,
    isCheckingAttorney,
    setIsCheckingAttorney,
    isGenerating,
    setIsGenerating,
    isLoading,
    setIsLoading,
    customerList,
    setCustomerList,
    states,
    setStates,
    errors,
    setErrors,
    customerErrors,
    setCustomerErrors,
    attorneyCount,
    setAttorneyCount,
    attorneyNumbers,
    setAttorneyNumbers,
    attorneyValidation,
    setAttorneyValidation,
    attorneyValidationMessages,
    setAttorneyValidationMessages,
    attorneyTMCounts,
    setAttorneyTMCounts,
  };
};

/**
 * How many trademarks a corporate subscription covers.
 *
 * The plan itself carries the number — the `tm_document_size` feature is what
 * "Monitor up to 10 trademarks" means in the plan list — so a Startup Pack is
 * 10 and an Enterprise Pack is 1,000 without anything being written here.
 * Attorneys are different: their count comes from the numbers they enter, so
 * this is only ever read for corporates.
 */
export const planTmDocumentSize = (plan) => {
  const feature = plan?.features?.find(
    (f) => f.feature_key === "tm_document_size",
  );
  const size = Number(feature?.default_value);
  return Number.isFinite(size) && size > 0 ? size : 0;
};

/* A free trial is a fixed sample of the product, not the plan the customer is
   sizing up, so it stays at five regardless of which plan is attached. */
export const CORPORATE_TRIAL_TM_COUNT = 5;

/**
 * Split a line amount into base, tax and total.
 *
 * Exported because this screen recomputes the money in several places — saving
 * an edited price, saving an edited TM count, applying a TM suggestion — and
 * each of those used to carry its own copy of the sum. They all added GST on
 * top, so the moment the amount was tax-inclusive those paths silently
 * overwrote the correct split with an exclusive one: the labels said "before
 * GST" while the figure beside them was the gross with tax added again.
 *
 * `amountHasGst` only means anything when GST applies at all; with the tax off
 * an inclusive amount is just the amount. With no rate loaded `gstFraction` is
 * 0, so nothing is split rather than a rate being invented.
 */
export const splitAmount = (lineAmount, { includeGST, amountHasGst, gstFraction }) => {
  const line = Number(lineAmount) || 0;
  const fraction = Number(gstFraction) || 0;
  const gross = Boolean(includeGST && amountHasGst);

  /* Inclusive: the base is backed out of the typed figure and the tax is what
     is left, so the total stays exactly what was entered rather than being
     rebuilt by adding a rounded tax back on. */
  const finalAmount = gross ? line / (1 + fraction) : line;
  const gstAmount = includeGST
    ? (gross ? line - finalAmount : finalAmount * fraction)
    : 0;

  return {
    finalAmount: Number(finalAmount).toFixed(2),
    gstAmount: Number(gstAmount).toFixed(2),
    totalAmount: Number(finalAmount + gstAmount).toFixed(2),
    enteredAmount: Number(line).toFixed(2),
    gstIncludedInAmount: gross,
  };
};

export const useOrderCalculations = (
  formData,
  activePlans,
  customerList,
  isEditingAmount,
  editablePlanAmount,
  attorneyNumbers,
  attorneyTMCounts,
  attorneyValidation,
  editOrderData,
  /* The live GST rate, as a percentage. Passed in rather than read here so the
     screen owns the loading state — until the master answers this is 0, and a
     quote is never inflated by a rate nobody confirmed. */
  gstPercent = 0,
) => {
  const gstFraction = (Number(gstPercent) || 0) / 100;
  const safeNumber = (value) => (isNaN(Number(value)) ? 0 : Number(value));

  const calculateOrderSummary = useCallback(() => {
    const {
      plan_id,
      plan_type,
      plan_price,
      customer_type,
      user_id,
      end_date,
      gst_inclusion,
      gst_included_in_amount,
      is_attorney,
    } = formData;

    if (customer_type === "2") {
      const selectedUser = customerList.find(
        (user) => user.id === Number(user_id),
      );

      let selectedPlan = activePlans.find(
        (plan) => plan.id === Number(plan_id),
      );

      let totalTMCount = Object.values(attorneyTMCounts).reduce(
        (sum, count) => sum + Number(count || 0),
        0,
      );
      if (is_attorney === "0") {
        totalTMCount = CORPORATE_TRIAL_TM_COUNT;
      }

      const attorneyDetails = attorneyNumbers
        .filter((num) => num.trim() !== "")
        .map((num) => ({
          attorneyNumber: num,
          tmCount: attorneyTMCounts[num] || 0,
          status: attorneyValidation[num] || "pending",
        }));

      return {
        customerName: selectedUser?.name || "-",
        customerEmail: selectedUser?.email || "-",
        customerMobile: selectedUser?.mobile || "-",
        customerType: "Free Trial",
        planName: selectedPlan?.name || "-",
        planType: "-",
        planAmount: 0,
        totalTMCount: totalTMCount,
        finalAmount: 0,
        gstAmount: 0,
        gstRate: Number(gstPercent) || 0,
        totalAmount: 0,
        includeGST: gst_inclusion,
        attorneyDetails: attorneyDetails,
        end_date: end_date || "-",
      };
    }

    const includeGST = gst_inclusion;

    let selectedPlan = activePlans.find((plan) => plan.id === Number(plan_id));

    let planAmount = isEditingAmount
      ? editablePlanAmount
      : plan_price
        ? Number(plan_price)
        : 0;

    if (!isEditingAmount && !plan_price && plan_id && plan_type) {
      if (selectedPlan) {
        planAmount =
          plan_type === "monthly"
            ? Number(selectedPlan.monthly_price)
            : Number(selectedPlan.annual_price);
      }
    }

    const attorneyDetails = attorneyNumbers
      .filter((num) => num.trim() !== "")
      .map((num) => ({
        attorneyNumber: num,
        tmCount: attorneyTMCounts[num] || 0,
        status: attorneyValidation[num] || "pending",
      }));

    let totalTMCount = Object.values(attorneyTMCounts).reduce(
      (sum, count) => sum + Number(count || 0),
      0,
    );

    /* Corporates have no attorney numbers to add up — the plan says how many
       trademarks are covered, and that is what the price multiplies. The rep can
       still override it from the summary; this is only the starting figure. */
    if (is_attorney === "0") {
      totalTMCount = planTmDocumentSize(selectedPlan);
    }

    /* What the per-unit price multiplies out to. Whether this figure is the
       taxable base or already has tax inside it is the next question, not this
       one. */
    const lineAmount =
      totalTMCount > 0 ? planAmount * totalTMCount : planAmount;

    const split = splitAmount(lineAmount, {
      includeGST,
      amountHasGst: gst_included_in_amount,
      gstFraction,
    });

    const selectedUser = customerList.find(
      (user) => user.id === Number(user_id),
    );
    if (!selectedPlan && editOrderData?.data?.plan_name) {
      selectedPlan = {
        name: editOrderData.data.plan_name,
      };
    }

    return {
      customerName: selectedUser?.name || "-",
      customerEmail: selectedUser?.email || "-",
      customerMobile: selectedUser?.mobile || "-",
      customerType:
        customer_type === "2"
          ? "Free Trial"
          : customer_type === "1"
            ? "Paid"
            : "-",
      planName: selectedPlan?.name || "-",
      planType:
        plan_type === "monthly"
          ? "Monthly"
          : plan_type === "annualy"
            ? "Annual"
            : "-",
      planAmount: Number(planAmount).toFixed(2),
      totalTMCount: totalTMCount,
      gstRate: Number(gstPercent) || 0,
      includeGST: includeGST,
      /* finalAmount, gstAmount, totalAmount, plus the basis the figures are on
         and the figure that was typed — all from the one splitter. */
      ...split,
      attorneyDetails: attorneyDetails,
      end_date: end_date || "-",
    };
  }, [
    formData.plan_id,
    formData.plan_type,
    formData.plan_price,
    formData.customer_type,
    formData.user_id,
    formData.end_date,
    formData.gst_inclusion,
    formData.gst_included_in_amount,
    formData.is_attorney,
    editablePlanAmount,
    isEditingAmount,
    attorneyTMCounts,
    attorneyNumbers,
    attorneyValidation,
    activePlans,
    customerList,
    gstFraction,
    gstPercent,
  ]);

  return { calculateOrderSummary, safeNumber };
};

/**
 * Unified attorney validation hook.
 *
 * FIX: Uses valid_attorneys / invalid_attorneys arrays from the API response
 * as the source of truth for EACH attorney individually.
 *
 * Previous bug: When API returned 409 "Already Exist" for attorney 1356,
 * the code was marking ALL attorneys (including valid 1344) as invalid
 * because it defaulted all to invalid and only overrode from the `details`
 * array — which is empty in the "Already Exist" response.
 *
 * Now: Each attorney is checked against valid_attorneys and invalid_attorneys
 * independently. If an attorney is in neither array (edge case), it's marked
 * as "already assigned".
 */
export const useAttorneyValidation = (
  setIsCheckingAttorney,
  setAttorneyValidation,
  setAttorneyValidationMessages,
  setAttorneyTMCounts,
  calculateOrderSummary,
  setAttorneyNameOptions,
) => {
  /**
   * Process API response — shared between try (200/409 in body) and catch (HTTP 409).
   *
   * Uses valid_attorneys / invalid_attorneys as source of truth per attorney.
   * Enriches valid attorneys with TM counts from details[] if available.
   */
  const processResponse = (filtered, responseData) => {
    const data = responseData?.data || {};
    const apiMessage = responseData?.message || "";

    const validSet = new Set(data.valid_attorneys || []);
    const invalidSet = new Set(data.invalid_attorneys || []);

    // Build TM count lookup from details (may be empty for "Already Exist" responses)
    const detailsMap = {};
    const nameOptions = {};
    (data.details || []).forEach((d) => {
      detailsMap[String(d.attorney_number)] = Number(d.count || 0);
      if (d.attorney_names?.length) {
        nameOptions[String(d.attorney_number)] = d.attorney_names;
      }
    });

    const validation = {};
    const validationMessages = {};
    const tmCounts = {};

    filtered.forEach((num) => {
      if (validSet.has(num)) {
        // ✅ Valid — attorney exists and is available
        const count = detailsMap[num] ?? 0;
        validation[num] = "valid";
        tmCounts[num] = count;
        validationMessages[num] = `Valid - ${count} TMs`;
      } else if (invalidSet.has(num)) {
        // ❌ Invalid — attorney number not found
        validation[num] = "invalid";
        tmCounts[num] = 0;
        validationMessages[num] = "Invalid Attorney Number";
      } else {
        // ⚠️ Not in either array — already assigned to another user
        validation[num] = "invalid";
        tmCounts[num] = 0;
        validationMessages[num] = apiMessage || "Attorney Number already assigned";
      }
    });

    return { validation, validationMessages, tmCounts, nameOptions };
  };

  const handleCheckAttorneyStatus = async (attorneyNumbers) => {
    const filtered = attorneyNumbers
      .map((v) => v.trim())
      .filter((v) => v !== "");

    if (filtered.length === 0) {
      toast.warning("Please enter at least one attorney number.");
      return;
    }

    const query = filtered.join(",");

    try {
      setIsCheckingAttorney(true);

      const res = await axios.get(
        `${API_ELSTIC_URL}/attorney/get-attorney-status-count?attorney_number=${query}`,
      );

      const { status, message } = res.data;

      if (status === 200 || status === 409) {
        const { validation, validationMessages, tmCounts, nameOptions } =
          processResponse(filtered, res.data);

        // Merge (don't replace) so existing attorneys in edit mode aren't lost
        setAttorneyValidation((prev) => ({ ...prev, ...validation }));
        setAttorneyValidationMessages((prev) => ({ ...prev, ...validationMessages }));
        setAttorneyTMCounts((prev) => ({ ...prev, ...tmCounts }));
        if (setAttorneyNameOptions) setAttorneyNameOptions((prev) => ({ ...prev, ...nameOptions }));

        // Toast feedback
        const validCount = filtered.filter((n) => validation[n] === "valid").length;
        const invalidCount = filtered.length - validCount;

        if (validCount === filtered.length) {
          toast.success(message || "All attorney numbers verified successfully!");
        } else if (validCount > 0) {
          toast.success(`${validCount} of ${filtered.length} attorney numbers verified`);
          if (invalidCount > 0) {
            toast.warning(`${invalidCount} attorney number(s) are invalid or already assigned`);
          }
        } else {
          toast.warning(message || "No valid attorney numbers found");
        }
      }
    } catch (error) {
      console.error("Error checking attorney status:", error);

      if (error.response?.status === 409) {
        const responseData = error.response.data;

        const { validation, validationMessages, tmCounts, nameOptions } =
          processResponse(filtered, responseData);

        // Merge (don't replace)
        setAttorneyValidation((prev) => ({ ...prev, ...validation }));
        setAttorneyValidationMessages((prev) => ({ ...prev, ...validationMessages }));
        setAttorneyTMCounts((prev) => ({ ...prev, ...tmCounts }));
        if (setAttorneyNameOptions) setAttorneyNameOptions((prev) => ({ ...prev, ...nameOptions }));

        // Toast feedback
        const validCount = filtered.filter((n) => validation[n] === "valid").length;
        const invalidCount = filtered.length - validCount;

        if (validCount > 0 && invalidCount > 0) {
          toast.success(`${validCount} attorney(s) verified`);
          toast.warning(
            responseData.message || `${invalidCount} attorney number(s) are invalid or already assigned`,
          );
        } else if (invalidCount > 0) {
          toast.warning(
            responseData.message || "Attorney number(s) are invalid or already assigned",
          );
        }
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to check attorney status.";
        toast.error(errorMessage);
      }
    } finally {
      setIsCheckingAttorney(false);
    }
  };

  return { handleCheckAttorneyStatus };
};
